import React, {
  useEffect,
  useState,
  useContext,
  useRef,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import logoWhite from "../assets/logo/white-logo.png";
import logoBlack from "../assets/logo/cork-matters-logo-2.svg";
import logoDarkGrey from "../assets/logo/dark-grey-logo.png";
import logoDarkNatural from "../assets/logo/dark-natural-logo.png";
import logoTransparent from "../assets/logo/transparent-logo.png";
import { IoMdHome } from "react-icons/io";
import styles from "./DropdownMenu.module.scss";
import {
  IoBagOutline,
  IoSearchOutline,
  IoChevronBackOutline,
  IoClose,
} from "react-icons/io5";
import { ShopContext } from "../context/ShopContext";
import { gsap } from "gsap";
import questionMark from "../assets/utility/questionmark.png";

const defaultSections = [
  { name: "Home", url: "/" },
  { name: "About us", url: "/about" },
  { name: "News", url: "/news" },
  { name: "Cork", url: "/cork" },
  { name: "Your Cork Project", url: "/project" },
  { name: "Downloads", url: "/downloads" },
  { name: "Contact", url: "/contact" },
];

const mobileSections = [
  { name: "Home", url: "/" },
  { name: "About us", url: "/about" },
  { name: "News", url: "/news" },
  { name: "Cork", url: "/cork" },
  { name: "Project", url: "/project" },
  { name: "Downloads", url: "/downloads" },
  { name: "Contact", url: "/contact" },
];

// Sort elements into reading order: top to bottom, then left to right within a
// row (tops within a few px count as the same row). Works for both the desktop
// columns and the stacked tablet/mobile layout.
const inReadingOrder = (els) => {
  const rows = [];
  els
    .map((el) => ({ el, rect: el.getBoundingClientRect() }))
    .sort((a, b) => a.rect.top - b.rect.top)
    .forEach((item) => {
      const row = rows[rows.length - 1];
      if (row && item.rect.top - row[0].rect.top < 8) row.push(item);
      else rows.push([item]);
    });
  return rows.flatMap((row) =>
    row.sort((a, b) => a.rect.left - b.rect.left).map((item) => item.el)
  );
};

// Menu photos fade in on a grey placeholder once downloaded, instead of a card
// fading in empty and the photo popping in later. Class-based (no state) so it
// never re-renders the menu; the ref covers photos already in the cache.
const markImageLoaded = (img) => img.classList.add(styles.imageLoaded);
const handleImageLoad = (e) => markImageLoaded(e.currentTarget);
const checkCachedImage = (img) => {
  if (img && img.complete && img.naturalWidth > 0) markImageLoaded(img);
};

const DropdownMenu = ({
  sections = defaultSections,
  theme = "darkTheme",
  iconColour = "black",
  align = "left",
  showElements,
  overideVisible,
  backVisible,
}) => {
  const [isCartIconAdded, setCartIconAdded] = useState(false);
  const { getTotalCartItemCount, products } = useContext(ShopContext);
  const [visible, setVisible] = useState(false);
  const [fadeDirection, setFadeDirection] = useState("in");
  const [shopDropdownVisible, setShopDropdownVisible] = useState(false);
  const [mobileNavVisible, setMobileNavVisible] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [hoveredCategory, setHoveredCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const shopRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);
  const openTlRef = useRef(null);
  const openedViaSearchRef = useRef(false);
  const mobileNavRef = useRef(null);
  const slideFromNavRef = useRef(false);

  let logoSrc;
  switch (theme) {
    case "lightTheme":
      logoSrc = logoWhite;
      break;
    case "transparentTheme":
      logoSrc = logoTransparent;
      break;
    case "darkTheme":
    default:
      logoSrc = logoBlack;
  }

  const navigate = useNavigate();

  const goBack = () => {
    setTimeout(() => {
      setVisible(true);
    }, 500);
    navigate(-1);
  };

  const handleMouseEnter = () => {
    setVisible(true);
    setFadeDirection("in");
  };

  const handleMouseLeave = () => {
    setFadeDirection("out");
    setVisible(false);
  };

  const handleToggle = () => {
    if (visible) {
      setFadeDirection("out");
      setVisible(false);
    } else {
      setFadeDirection("in");
      setVisible(true);
    }
  };

  useEffect(() => {
    if (overideVisible === true) {
      setFadeDirection("in");
      setVisible(true);
    } else if (overideVisible === false) {
      setFadeDirection("out");
      setVisible(false);
    }
  }, [overideVisible]);

  const handleShopClick = (e) => {
    e.preventDefault();
    setShopDropdownVisible(true);
  };

  // The search icon opens the same panel, then puts the cursor in the search
  // field once the entrance animation has finished.
  const handleSearchClick = (e) => {
    openedViaSearchRef.current = true;
    handleShopClick(e);
  };

  // On mobile, tapping the logo opens the full-screen nav overlay instead of
  // the small stacked buttons (which are hidden ≤500px). Desktop keeps its
  // hover-to-reveal behaviour untouched.
  const handleLogoActivate = () => {
    if (isMobile) {
      setMobileNavVisible(true);
    }
  };

  const handleMobileNavClose = () => {
    if (mobileNavRef.current) {
      gsap.to(mobileNavRef.current, {
        opacity: 0,
        duration: 0.45,
        ease: "power2.inOut",
        onComplete: () => setMobileNavVisible(false),
      });
    } else {
      setMobileNavVisible(false);
    }
  };

  // "Shop" inside the full-screen nav transitions to the shop menu: the links
  // slide out to the right (white background held in place so the page never
  // shows through) while the shop panel slides in over them from the right.
  const handleMobileShopClick = () => {
    slideFromNavRef.current = true;
    setShopDropdownVisible(true);

    if (mobileNavRef.current) {
      // Slide ONLY the links out — leave the header (logo + close) in place so
      // the logo never animates. The shop panel's logo is pinned to the same
      // spot (see the slide-in timeline below), so it looks like one static
      // logo the whole way through.
      // getElementsByClassName (not a selector) — CSS-module hashes can
      // contain characters like "+" that break querySelector.
      const links =
        mobileNavRef.current.getElementsByClassName(styles.mobileNavLinks)[0];
      if (links) {
        gsap.to(links, {
          xPercent: 40,
          opacity: 0,
          duration: 0.8,
          ease: "power2.in",
        });
      }
      // Keep the white overlay mounted until the shop panel has fully covered
      // it, so the background behind never flashes into view.
      gsap.delayedCall(1.0, () => setMobileNavVisible(false));
    } else {
      setMobileNavVisible(false);
    }
  };

  // Reverse of the entrance: the panel lifts away as the backdrop clears.
  const handleCloseShop = useCallback(() => {
    if (!shopRef.current) {
      setShopDropdownVisible(false);
      return;
    }
    // Stop an entrance that's still running so it can't fight the exit.
    openTlRef.current?.kill();
    openedViaSearchRef.current = false;
    gsap
      .timeline({ onComplete: () => setShopDropdownVisible(false) })
      .to(
        panelRef.current,
        { y: -16, opacity: 0, duration: 0.35, ease: "power2.in" },
        0
      )
      .to(
        shopRef.current,
        {
          backgroundColor: "rgba(0, 0, 0, 0)",
          duration: 0.35,
          ease: "power2.in",
        },
        0
      );
  }, []);

  // Helper to close everything when a link is clicked
  const handleNavigationClose = () => {
    handleCloseShop();
    setVisible(false);
    setFadeDirection("out");
  };

  useEffect(() => {
    if (shopDropdownVisible && shopRef.current) {
      if (slideFromNavRef.current) {
        // Coming from the mobile nav: slide + fade the panel in from the right,
        // then stagger the categories and product items in.
        slideFromNavRef.current = false;
        // Collect every element tagged for staggering, in DOM order (categories
        // first, then search + bestsellers, top to bottom), and drop anything
        // hidden at this breakpoint (e.g. the desktop categories list, which is
        // display:none on mobile) so it doesn't waste stagger steps.
        const items = [
          ...shopRef.current.querySelectorAll("[data-stagger]"),
        ].filter((el) => el.offsetParent !== null);
        // Pin the shop logo so it doesn't ride in with the panel: counter the
        // panel's translate by the same absolute distance (panel width == the
        // viewport width). Together with the mobile nav's logo staying put,
        // the logo looks completely static across the transition.
        const shopLogo = shopRef.current.querySelector("[data-shop-logo]");
        const tl = gsap.timeline();
        tl.set(items, { opacity: 0, y: 24 });
        tl.fromTo(
          shopRef.current,
          { xPercent: 100, opacity: 0 },
          { xPercent: 0, opacity: 1, duration: 1.0, ease: "power3.out" }
        );
        if (shopLogo) {
          tl.fromTo(
            shopLogo,
            { x: -window.innerWidth },
            { x: 0, duration: 1.0, ease: "power3.out" },
            "<"
          );
        }
        tl.to(
          items,
          {
            opacity: 1,
            y: 0,
            duration: 0.8,
            stagger: 0.1,
            ease: "power2.out",
          },
          "-=0.4"
        );
        openTlRef.current = tl;
      } else {
        // Direct open — the desktop Shop button, or the search icon on any
        // screen: the backdrop dims and the panel eases down, then its
        // contents fade up one by one in reading order.
        const STAGGER = 0.15; // seconds between each item
        const ITEMS_START = 0.3; // once the panel has mostly settled
        const overlay = shopRef.current;
        const items = inReadingOrder(
          [...overlay.querySelectorAll("[data-stagger]")].filter(
            (el) => el.offsetParent !== null
          )
        );
        // Opened from the search icon: the search bar comes in first.
        const header = openedViaSearchRef.current
          ? searchInputRef.current?.closest("[data-stagger]")
          : null;
        openedViaSearchRef.current = false;
        const order = header
          ? [header, ...items.filter((el) => el !== header)]
          : items;

        // Read the backdrop colour from the CSS before zeroing it, so the
        // stylesheet stays the source of truth for how dark it gets.
        const backdrop = getComputedStyle(overlay).backgroundColor;
        gsap.set(overlay, { opacity: 1, backgroundColor: "rgba(0, 0, 0, 0)" });
        gsap.set(order, { opacity: 0, y: 24 });

        const tl = gsap.timeline();
        tl.to(
          overlay,
          { backgroundColor: backdrop, duration: 0.6, ease: "power2.out" },
          0
        )
          .fromTo(
            panelRef.current,
            { y: -24, opacity: 0 },
            {
              y: 0,
              opacity: 1,
              duration: 0.7,
              ease: "power3.out",
              clearProps: "transform,opacity",
            },
            0
          )
          .to(
            order,
            {
              opacity: 1,
              y: 0,
              duration: 0.8,
              stagger: STAGGER,
              ease: "power2.out",
              clearProps: "transform,opacity",
            },
            ITEMS_START
          );
        if (header) {
          // Put the cursor in the search field as it fades in.
          tl.call(
            () => searchInputRef.current?.focus({ preventScroll: true }),
            null,
            ITEMS_START + 0.3
          );
        }
        openTlRef.current = tl;
      }
    }
  }, [shopDropdownVisible]);

  // Escape closes the shop menu.
  useEffect(() => {
    if (!shopDropdownVisible) return;
    const onKeyDown = (e) => {
      if (e.key === "Escape") handleCloseShop();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [shopDropdownVisible, handleCloseShop]);

  useEffect(() => {
    if (mobileNavVisible && mobileNavRef.current) {
      gsap.fromTo(
        mobileNavRef.current,
        { opacity: 0 },
        { opacity: 1, duration: 0.45, ease: "power2.out" }
      );
    }
  }, [mobileNavVisible]);

  // Lock background scroll while a full-screen overlay (shop menu or mobile
  // nav) is open, so the page behind doesn't scroll under it.
  useEffect(() => {
    if (shopDropdownVisible || mobileNavVisible) {
      const root = document.documentElement;
      const prevOverflow = root.style.overflow;
      root.style.overflow = "hidden";
      return () => {
        root.style.overflow = prevOverflow;
      };
    }
  }, [shopDropdownVisible, mobileNavVisible]);

  const categoryItems = [
    { name: "Lighting", url: "/shop#lighting" },
    { name: "Home living", url: "/shop#home-living" },
    { name: "Office", url: "/shop#office" },
    { name: "Tableware", url: "/shop#tableware" },
    { name: "EVA collection", url: "/shop#eva-collection" },
  ];

  const categoriesA = categoryItems.slice(0, 3);
  const categoriesB = categoryItems.slice(3);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 500);
    };
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  const currentSections = isMobile ? mobileSections : sections;

  const filteredProducts = products.filter((p) =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const bestsellerProducts = isMobile
    ? products.slice(12, 18)
    : products.slice(20, 25);

  const displayProducts = searchQuery
    ? filteredProducts.slice(0, isMobile ? 6 : 5)
    : bestsellerProducts;

  // Warm the browser cache with the bestseller photos once the page itself has
  // finished loading, so they're ready before the menu is opened — otherwise
  // they only start downloading on open and arrive out of order mid-animation.
  const bestsellerPhotoKey = bestsellerProducts
    .map((p) => p.variations[0]?.coverPhoto)
    .filter(Boolean)
    .join("|");
  useEffect(() => {
    if (!bestsellerPhotoKey) return;
    const preload = () =>
      bestsellerPhotoKey.split("|").forEach((src) => {
        const img = new Image();
        img.src = src;
      });
    if ("requestIdleCallback" in window) {
      const id = window.requestIdleCallback(preload, { timeout: 3000 });
      return () => window.cancelIdleCallback(id);
    }
    const id = setTimeout(preload, 1500);
    return () => clearTimeout(id);
  }, [bestsellerPhotoKey]);

  const formatPrice = (price) => {
    return Number(price).toLocaleString("de-DE", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  return (
    <div>
      <div
        className={`${styles.lockedPositionMenu} ${styles[align]} ${showElements}`}
      >
        <div className={`${styles.logoWithButtonsContainer} ${styles[align]}`}>
          <div
            className={styles.verticalMenuContainer}
            style={{
              pointerEvents: showElements ? "auto" : "none",
            }}
            onMouseLeave={handleMouseLeave}
          >
            <div
              to="/"
              className={styles.logoLinkPointer}
              style={{
                pointerEvents: showElements ? "auto" : "none",
              }}
            >
              <div
                onClick={handleLogoActivate}
                onMouseEnter={handleMouseEnter}
                className={styles.logoPointer}
              >
                <img src={logoSrc} alt="Logo" />
              </div>
            </div>
            <div
              className={styles.logoButtonsContainer}
              style={{
                pointerEvents: visible ? "auto" : "none",
              }}
            >
              <Link to={"/shop"} key={0}>
                <div
                  className={`${styles.logoButton} ${
                    visible ? styles.fadeIn : styles.fadeOut
                  } ${styles[theme]} ${styles.redBackground}`}
                  style={{
                    transitionDelay: `${
                      fadeDirection === "in"
                        ? 0 * 100
                        : (currentSections.length - 1 - 0) * 100
                    }ms`,
                  }}
                  onClick={handleShopClick}
                >
                  Shop
                </div>
              </Link>
              {currentSections.map((section, index) => (
                <Link to={section.url} key={index + 1}>
                  <div
                    className={`${styles.logoButton} ${
                      visible ? styles.fadeIn : styles.fadeOut
                    } ${styles[theme]} ${styles.blackBackground}`}
                    style={{
                      transitionDelay: `${
                        fadeDirection === "in"
                          ? (index + 1) * 100
                          : (currentSections.length - 1 - (index + 1)) * 100
                      }ms`,
                    }}
                  >
                    {section.name}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
      <div
        className={`${styles.lockedPositionIconsContainer} ${styles.right} ${showElements}`}
      >
        <div className={styles.lockedPositionIcons}>
          <Link onClick={handleSearchClick}>
            <span
              className={`${styles.lockedPositionSearchIcon} ${styles[iconColour]}`}
            >
              <IoSearchOutline />
            </span>
          </Link>
          <Link to="/cart" className={styles.cartLink}>
            <span className={styles.cartIconWrapper}>
              <span
                className={`${styles.lockedPositionCartIcon} ${
                  isCartIconAdded ? styles.cartIconAdded : ""
                } ${theme === "dark" ? styles.darkThemeText : ""} ${
                  styles[iconColour]
                }`}
              >
                <IoBagOutline />
              </span>
              {getTotalCartItemCount() > 0 && (
                <span className={styles.cartItemCount}>
                  {getTotalCartItemCount()}
                </span>
              )}
            </span>
          </Link>
        </div>
      </div>
      {mobileNavVisible && (
        <div
          ref={mobileNavRef}
          className={styles.mobileNavOverlay}
          style={{ opacity: 0 }}
        >
          <div className={styles.mobileNavHeader}>
            <button
              type="button"
              className={styles.mobileNavLogo}
              onClick={handleMobileNavClose}
              aria-label="Close menu"
            >
              <img src={logoSrc} alt="Cork Matters" />
            </button>
            <button
              type="button"
              className={styles.mobileNavClose}
              onClick={handleMobileNavClose}
              aria-label="Close menu"
            >
              <IoClose />
            </button>
          </div>
          <nav className={styles.mobileNavLinks}>
            <button
              type="button"
              className={`${styles.mobileNavLink} ${styles.mobileNavShop}`}
              onClick={handleMobileShopClick}
            >
              Shop
            </button>
            {mobileSections.map((section) => (
              <Link
                to={section.url}
                key={section.name}
                className={styles.mobileNavLink}
                onClick={handleMobileNavClose}
              >
                {section.name}
              </Link>
            ))}
          </nav>
        </div>
      )}
      {shopDropdownVisible && (
        <div
          ref={shopRef}
          className={styles.shopDropdownOverlay}
          style={{ opacity: 0 }}
          onClick={handleCloseShop}
        >
          <div
            ref={panelRef}
            className={`${styles.shopDropdownContent}`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className={`${styles.menuSections} ${styles.grid}`}>
              <div className={styles.leftContainer}>
                <Link
                  to="/"
                  data-shop-logo
                  className={`${styles.logoLinkPointer} ${styles.paddingLogoTop}`}
                  style={{
                    pointerEvents: showElements ? "auto" : "none",
                  }}
                  onClick={handleNavigationClose}
                >
                  <div
                    onTouchStart={handleToggle}
                    onMouseEnter={handleMouseEnter}
                    className={styles.logoPointer}
                  >
                    <img src={logoSrc} alt="Logo" />
                  </div>
                </Link>
                <div className={styles.closeButtonMobileContainer}>
                  <div
                    className={styles.closeButtonMobile}
                    onClick={handleCloseShop}
                    aria-label="Close menu"
                  >
                    <IoClose />
                  </div>
                </div>
                <div className={styles.categoriesContainer}>
                  <div className={styles.categoriesTitle} data-stagger>Categories</div>
                  <div className={styles.categories}>
                    {categoryItems.map((cat) => (
                      <Link
                        to={cat.url}
                        key={cat.name}
                        onClick={handleNavigationClose}
                      >
                        <div
                          className={styles.category}
                          data-stagger
                          onMouseEnter={() => setHoveredCategory(cat.name)}
                        >
                          {cat.name}
                        </div>
                      </Link>
                    ))}
                  </div>
                  <div className={styles.viewAll} data-stagger>
                    <div className={styles.linkContainer}>
                      <Link
                        to="/products/all#lighting"
                        className={`${styles.isAlt} ${styles.underlineLink} ${styles.fontSize14}`}
                        onClick={handleNavigationClose}
                      >
                        View all
                      </Link>
                    </div>
                  </div>
                </div>
                <div className={styles.categoriesContainerMobile}>
                  <div className={styles.categoriesTitle} data-stagger>Categories</div>
                  <div className={styles.categoriesWrapper}>
                    <div className={styles.categories}>
                      {categoriesA.map((cat) => (
                        <Link
                          to={cat.url}
                          key={cat.name}
                          onClick={handleNavigationClose}
                        >
                          <div
                            className={styles.category}
                          data-stagger
                            onMouseEnter={() => setHoveredCategory(cat.name)}
                          >
                            {cat.name}
                          </div>
                        </Link>
                      ))}
                    </div>
                    <div className={styles.categories}>
                      {categoriesB.map((cat) => (
                        <Link
                          to={cat.url}
                          key={cat.name}
                          onClick={handleNavigationClose}
                        >
                          <div
                            className={styles.category}
                          data-stagger
                            onMouseEnter={() => setHoveredCategory(cat.name)}
                          >
                            {cat.name}
                          </div>
                        </Link>
                      ))}
                      <div className={styles.viewAll} data-stagger>
                        <div className={styles.linkContainer}>
                          <Link
                            to="/products/all"
                            className={`${styles.isAlt} ${styles.underlineLink} ${styles.fontSize14}`}
                            onClick={handleNavigationClose}
                          >
                            View all
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className={styles.bestsellers}>
                <div className={styles.closeButtonContainer}>
                  <button
                    type="button"
                    className={styles.closeButton}
                    onClick={handleCloseShop}
                    aria-label="Close shop menu"
                  >
                    <IoClose />
                  </button>
                </div>
                <div className={styles.bestsellersHeader} data-stagger>
                  <div className={styles.bestsellersTitle}>
                    {searchQuery ? "Search Results" : "Bestsellers"}
                  </div>
                  <div className={styles.searchBar}>
                    <input
                      ref={searchInputRef}
                      type="text"
                      placeholder="Search..."
                      aria-label="Search products"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                    <IoSearchOutline className={styles.searchIcon} />
                  </div>
                </div>
                {displayProducts.map((suggestedProduct, index) => (
                  <Link
                    to={`/product/${suggestedProduct.id}`}
                    key={suggestedProduct.id}
                    className={styles.bestsellerItem} data-stagger
                    onClick={handleNavigationClose}
                  >
                    <div className={styles.bestsellerImage}>
                      <img
                        src={suggestedProduct.variations[0].coverPhoto}
                        alt={suggestedProduct.name}
                        onLoad={handleImageLoad}
                        ref={checkCachedImage}
                      />
                    </div>
                    <div className={styles.bestsellerTitle}>
                      {suggestedProduct.name} {" "}
                      {suggestedProduct.size
                        ? `(${suggestedProduct.size})`
                        : ""}
                    </div>
                    <div className={styles.bestsellerPrice}>
                      €{formatPrice(suggestedProduct.variations[0].price)}
                    </div>
                  </Link>
                ))}
                {searchQuery && filteredProducts.length === 0 && (
                  <div className={styles.bestsellerItem} data-stagger>
                    <img src={questionMark} alt="No item found" />
                    <div className={styles.bestsellerTitle}>
                      No Product Found
                    </div>
                    <div className={styles.bestsellerPrice}>€--,--</div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DropdownMenu;
